/**
 * MUTAGIM.SHOP — Promo Marquee Component
 * Bandeau promotionnel défilant
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PromoMarquee() {
  const { i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const lang = i18n.language?.split('-')[0] || 'fr';

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await fetch(`${API_URL}/api/promo-marquee`);
        if (res.ok) {
          const result = await res.json();
          setData(result);
        }
      } catch (err) {
        console.error('Error fetching promo marquee:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  if (loading || !data?.enabled || !data?.messages?.length) {
    // Don't render if loading, disabled, or no messages
    return null;
  }

  const { messages, settings } = data;
  const separator = settings?.separator || '•';
  
  // Create the text content with separators
  const createMessageContent = (msg) => {
    const text = msg.text?.[lang] || msg.text?.fr || '';
    return (
      <span 
        className="inline-flex items-center gap-2"
        style={{ color: msg.highlight_color || settings?.text_color || '#ffffff' }}
      >
        {msg.icon && <span className="text-lg">{msg.icon}</span>}
        {msg.link_url ? (
          <a 
            href={msg.link_url} 
            className="hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {text}
          </a>
        ) : (
          <span>{text}</span>
        )}
      </span>
    );
  };
  
  // Duplicate messages for seamless loop
  const duplicatedMessages = [...messages, ...messages, ...messages];

  return (
    <div 
      className="w-full overflow-hidden py-2"
      style={{ backgroundColor: settings?.background_color || '#111111' }}
      data-testid="promo-marquee"
    >
      <div className="relative">
        <div 
          className="flex items-center gap-8 animate-promo-marquee whitespace-nowrap"
          style={{
            animationDuration: `${settings?.speed || 20}s`,
            color: settings?.text_color || '#ffffff'
          }}
        >
          {duplicatedMessages.map((msg, index) => (
            <React.Fragment key={`${msg.id}-${index}`}>
              {createMessageContent(msg)}
              <span className="text-gray-500 mx-4">{separator}</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes promo-marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        .animate-promo-marquee {
          animation: promo-marquee linear infinite;
          width: max-content;
        }
        .animate-promo-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}

export default PromoMarquee;
